/**
 * figmaExport.ts — DOM-to-SVG converter for Figma paste (v4.5)
 *
 * Uses Range.getClientRects() to extract EXACT rendered text positions.
 * Includes:
 *   - Background & border capture with CSS Color Level 4 support
 *   - Real CSS gradient → SVG gradient conversion
 *   - Box-shadow → SVG filter blur (parsed from actual CSS values)
 *   - Outline rendering
 *   - SVG element serialization (Lucide icons etc.)
 *   - Raster image embed / placeholder
 *   - Video/iframe placeholder rectangles
 *   - Text via TreeWalker + Range (exact browser positions)
 *   - Text-decoration (underline, line-through)
 *   - Auto contrast fix: if text blends into background, flip to white/black
 *   - Overflow clipping support
 */

/* ── Types ── */

interface RectPrim {
  tag: "rect";
  x: number; y: number; w: number; h: number;
  fill?: string; stroke?: string; strokeW?: number;
  rx?: number; opacity?: number;
  filter?: string;
  clipId?: string;
}

interface TextPrim {
  tag: "text";
  x: number; y: number;
  content: string;
  fontSize: number;
  fontWeight: string;
  fontFamily: string;
  fill: string;
  textDecoration?: string;
}

interface ImagePrim {
  tag: "image";
  x: number; y: number; w: number; h: number;
  href: string;
  clipId?: string;
}

interface GradientRectPrim {
  tag: "gradientRect";
  x: number; y: number; w: number; h: number;
  gradientId: string;
  rx?: number; opacity?: number;
}

type Prim = RectPrim | TextPrim | ImagePrim | GradientRectPrim;

interface GradientDef {
  id: string;
  type: "linear" | "radial";
  angle?: number; // degrees for linear
  stops: Array<{ offset: string; color: string }>;
}

interface FilterDef {
  id: string;
  blur: number;
  dx: number;
  dy: number;
}

interface ClipDef {
  id: string;
  x: number; y: number; w: number; h: number;
  rx: number;
}

export interface FigmaExportOptions {
  includeImages?: boolean;
  mode?: "viewport" | "fullpage";
}

/* ── Shared state for defs ── */

let _gradients: GradientDef[] = [];
let _filters: FilterDef[] = [];
let _clips: ClipDef[] = [];
let _gradientCounter = 0;
let _filterCounter = 0;
let _clipCounter = 0;

function resetDefs() {
  _gradients = [];
  _filters = [];
  _clips = [];
  _gradientCounter = 0;
  _filterCounter = 0;
  _clipCounter = 0;
}

/* ── Skip logic ── */

const SKIP_ATTRS = [
  "data-edit-toolbar", "data-comment-pin", "data-comment-panel",
  "data-block-palette", "data-admin-bar", "data-screenshot-modal",
  "data-figma-export-modal",
];

function isSkipped(el: Element): boolean {
  if (!(el instanceof HTMLElement)) return false;
  for (const a of SKIP_ATTRS) if (el.hasAttribute(a)) return true;
  if (el.getAttribute("data-tour-step") === "admin-bar") return true;
  const tag = el.tagName;
  if (tag === "SCRIPT" || tag === "STYLE" || tag === "NOSCRIPT") return true;
  try {
    const s = window.getComputedStyle(el);
    if (s.display === "none" || s.visibility === "hidden") return true;
    if (parseFloat(s.opacity) < 0.02) return true;
  } catch { /* */ }
  return false;
}

function ancestorSkipped(el: Element | null): boolean {
  let c = el;
  while (c && c !== document.body) {
    if (c instanceof HTMLElement && isSkipped(c)) return true;
    c = c.parentElement;
  }
  return false;
}

/* ── Color helpers (CSS Color Level 4 support) ── */

let _colorCtx: CanvasRenderingContext2D | null = null;
function getColorCtx(): CanvasRenderingContext2D | null {
  if (!_colorCtx) {
    const c = document.createElement("canvas");
    c.width = 1; c.height = 1;
    _colorCtx = c.getContext("2d");
  }
  return _colorCtx;
}

function parseColor(raw: string): string | null {
  if (!raw || raw === "transparent" || raw === "rgba(0, 0, 0, 0)" || raw === "rgb(0 0 0 / 0)") return null;

  // 1. Comma-separated rgb/rgba: rgb(255, 255, 255) / rgba(0, 0, 0, 0.5)
  const mComma = raw.match(/rgba?\(\s*(\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/);
  if (mComma) {
    const a = mComma[4] !== undefined ? parseFloat(mComma[4]) : 1;
    if (a < 0.02) return null;
    return "#" + [mComma[1], mComma[2], mComma[3]].map(c => parseInt(c).toString(16).padStart(2, "0")).join("");
  }

  // 2. Space-separated rgb (CSS Color Level 4): rgb(229 231 235) / rgb(229 231 235 / 0.5)
  const mSpace = raw.match(/rgba?\(\s*(\d+)\s+(\d+)\s+(\d+)(?:\s*\/\s*([\d.]+%?))?\s*\)/);
  if (mSpace) {
    const a = mSpace[4] !== undefined ? (mSpace[4].endsWith("%") ? parseFloat(mSpace[4]) / 100 : parseFloat(mSpace[4])) : 1;
    if (a < 0.02) return null;
    return "#" + [mSpace[1], mSpace[2], mSpace[3]].map(c => parseInt(c).toString(16).padStart(2, "0")).join("");
  }

  // 3. Hex
  if (raw.startsWith("#")) return raw;

  // 4. Canvas fallback — handles oklch(), hsl(), color(), lab(), lch(), named colors, etc.
  try {
    const ctx = getColorCtx();
    if (ctx) {
      ctx.clearRect(0, 0, 1, 1);
      ctx.fillStyle = "rgba(0,0,0,0)";
      ctx.fillStyle = raw;
      ctx.fillRect(0, 0, 1, 1);
      const px = ctx.getImageData(0, 0, 1, 1).data;
      if (px[3] < 5) return null;
      return "#" + [px[0], px[1], px[2]].map(c => c.toString(16).padStart(2, "0")).join("");
    }
  } catch { /* */ }

  return null;
}

function getAlpha(raw: string): number {
  const mComma = raw.match(/rgba?\([^)]+,\s*([\d.]+)\s*\)/);
  if (mComma) return parseFloat(mComma[1]);
  const mSpace = raw.match(/rgba?\([^)]+\/\s*([\d.]+%?)\s*\)/);
  if (mSpace) return mSpace[1].endsWith("%") ? parseFloat(mSpace[1]) / 100 : parseFloat(mSpace[1]);
  try {
    const ctx = getColorCtx();
    if (ctx) {
      ctx.clearRect(0, 0, 1, 1);
      ctx.fillStyle = "rgba(0,0,0,0)";
      ctx.fillStyle = raw;
      ctx.fillRect(0, 0, 1, 1);
      return ctx.getImageData(0, 0, 1, 1).data[3] / 255;
    }
  } catch { /* */ }
  return 1;
}

function bRadius(s: CSSStyleDeclaration): number {
  const v = parseFloat(s.borderRadius);
  return isNaN(v) ? 0 : Math.min(v, 999);
}

/* ── Luminance & contrast ── */

function hexToSrgb(hex: string): [number, number, number] {
  const h = hex.replace("#", "");
  if (h.length < 6) return [0, 0, 0];
  return [
    parseInt(h.substring(0, 2), 16) / 255,
    parseInt(h.substring(2, 4), 16) / 255,
    parseInt(h.substring(4, 6), 16) / 255,
  ];
}

function luminance(hex: string): number {
  const [r, g, b] = hexToSrgb(hex).map(c =>
    c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
  );
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

function contrastRatio(l1: number, l2: number): number {
  const hi = Math.max(l1, l2);
  const lo = Math.min(l1, l2);
  return (hi + 0.05) / (lo + 0.05);
}

/* darken a hex color by a factor (0=black, 1=no change) */
function darkenHex(hex: string, factor: number): string {
  const [r, g, b] = hexToSrgb(hex);
  const dr = Math.round(r * factor * 255);
  const dg = Math.round(g * factor * 255);
  const db = Math.round(b * factor * 255);
  return "#" + [dr, dg, db].map(c => Math.max(0, Math.min(255, c)).toString(16).padStart(2, "0")).join("");
}

/* ── Gradient parsing ── */

function parseGradient(bgImage: string, x: number, y: number, w: number, h: number): GradientRectPrim | null {
  if (!bgImage || bgImage === "none") return null;

  const isLinear = bgImage.includes("linear-gradient");
  const isRadial = bgImage.includes("radial-gradient");
  if (!isLinear && !isRadial) return null;

  // Extract the content inside the gradient function
  const funcMatch = bgImage.match(/(linear|radial)-gradient\((.+)\)/s);
  if (!funcMatch) return null;

  const inner = funcMatch[2];

  // Parse angle for linear gradients
  let angle = 180; // default: top to bottom
  if (isLinear) {
    const angleMatch = inner.match(/^(\d+(?:\.\d+)?)deg/);
    if (angleMatch) {
      angle = parseFloat(angleMatch[1]);
    } else if (inner.startsWith("to ")) {
      const dirMatch = inner.match(/^to\s+(\w+(?:\s+\w+)?)/);
      if (dirMatch) {
        const dir = dirMatch[1];
        const dirMap: Record<string, number> = {
          "top": 0, "right": 90, "bottom": 180, "left": 270,
          "top right": 45, "right top": 45,
          "bottom right": 135, "right bottom": 135,
          "bottom left": 225, "left bottom": 225,
          "top left": 315, "left top": 315,
        };
        if (dirMap[dir] !== undefined) angle = dirMap[dir];
      }
    }
  }

  // Extract color stops — match various color formats
  // Strategy: split by commas that are outside of parentheses, then parse each segment
  const segments = splitGradientArgs(inner);

  const stops: Array<{ offset: string; color: string }> = [];
  let colorSegments = segments;

  // Skip the first segment if it's a direction/angle
  if (isLinear && (segments[0].match(/^\d+(\.\d+)?deg/) || segments[0].startsWith("to "))) {
    colorSegments = segments.slice(1);
  }
  if (isRadial && (segments[0].includes("circle") || segments[0].includes("ellipse") || segments[0].includes("at "))) {
    colorSegments = segments.slice(1);
  }

  for (let i = 0; i < colorSegments.length; i++) {
    const seg = colorSegments[i].trim();
    if (!seg) continue;

    // Try to extract percentage from the end
    const pctMatch = seg.match(/\s+(\d+(?:\.\d+)?%)\s*$/);
    let pct: string;
    let colorStr: string;

    if (pctMatch) {
      pct = pctMatch[1];
      colorStr = seg.slice(0, seg.length - pctMatch[0].length).trim();
    } else {
      // Auto-distribute
      pct = colorSegments.length === 1 ? "50%" : `${Math.round((i / Math.max(1, colorSegments.length - 1)) * 100)}%`;
      colorStr = seg;
    }

    const hex = parseColor(colorStr);
    if (hex) {
      stops.push({ offset: pct, color: hex });
    }
  }

  if (stops.length < 2) return null;

  const gradId = `grad_${++_gradientCounter}`;
  _gradients.push({
    id: gradId,
    type: isLinear ? "linear" : "radial",
    angle: isLinear ? angle : undefined,
    stops,
  });

  return {
    tag: "gradientRect",
    x, y, w, h,
    gradientId: gradId,
  };
}

/** Split gradient arguments by commas, respecting nested parentheses */
function splitGradientArgs(s: string): string[] {
  const parts: string[] = [];
  let depth = 0;
  let start = 0;
  for (let i = 0; i < s.length; i++) {
    if (s[i] === "(") depth++;
    else if (s[i] === ")") depth--;
    else if (s[i] === "," && depth === 0) {
      parts.push(s.substring(start, i).trim());
      start = i + 1;
    }
  }
  parts.push(s.substring(start).trim());
  return parts;
}

/* ── Box-shadow parsing ── */

interface ParsedShadow {
  inset: boolean;
  x: number;
  y: number;
  blur: number;
  spread: number;
  color: string;
}

function parseBoxShadows(raw: string): ParsedShadow[] {
  if (!raw || raw === "none") return [];

  const shadows: ParsedShadow[] = [];

  // Split by commas that are outside parentheses
  const parts = splitGradientArgs(raw);

  for (const part of parts) {
    const trimmed = part.trim();
    if (!trimmed) continue;

    const inset = trimmed.includes("inset");
    const withoutInset = trimmed.replace(/inset/g, "").trim();

    // Extract the color (could be at start or end, in any format)
    // Strategy: try to find numeric values (px values), rest is color
    const tokens = withoutInset.split(/\s+/);
    const numericTokens: number[] = [];
    const colorTokens: string[] = [];
    let inColorFunc = false;
    let colorFuncStr = "";

    for (const token of tokens) {
      if (inColorFunc) {
        colorFuncStr += " " + token;
        if (token.includes(")")) {
          inColorFunc = false;
          colorTokens.push(colorFuncStr);
          colorFuncStr = "";
        }
        continue;
      }

      // Check if this starts a color function
      if (token.match(/^(rgb|rgba|hsl|hsla|oklch|oklab|lab|lch|color|hwb)\(/i)) {
        inColorFunc = !token.includes(")");
        if (inColorFunc) {
          colorFuncStr = token;
        } else {
          colorTokens.push(token);
        }
        continue;
      }

      const numVal = parseFloat(token);
      if (!isNaN(numVal) && (token.endsWith("px") || token.match(/^-?\d+(\.\d+)?$/))) {
        numericTokens.push(numVal);
      } else if (token.startsWith("#") || token.match(/^[a-z]+$/i)) {
        colorTokens.push(token);
      }
    }

    const x = numericTokens[0] || 0;
    const y = numericTokens[1] || 0;
    const blur = numericTokens[2] || 0;
    const spread = numericTokens[3] || 0;
    const colorRaw = colorTokens.join(" ") || "rgba(0,0,0,0.25)";
    const color = parseColor(colorRaw) || "#00000040";

    shadows.push({ inset, x, y, blur, spread, color });
  }

  return shadows;
}

function getOrCreateFilter(blur: number, dx: number, dy: number): string {
  // Reuse existing filter with same params
  const stdDev = Math.max(blur / 2, 0.5);
  const existing = _filters.find(f =>
    Math.abs(f.blur - stdDev) < 0.1 && Math.abs(f.dx - dx) < 0.1 && Math.abs(f.dy - dy) < 0.1
  );
  if (existing) return existing.id;

  const id = `shadow_${++_filterCounter}`;
  _filters.push({ id, blur: stdDev, dx, dy });
  return id;
}

function getOrCreateClip(x: number, y: number, w: number, h: number, rx: number): string {
  const id = `clip_${++_clipCounter}`;
  _clips.push({ id, x, y, w, h, rx });
  return id;
}

/* ══════════════════════════════════════════
   Pass 1 — Backgrounds, Borders, Shadows, Outlines
   ══════════════════════════════════════════ */

function collectBg(
  el: HTMLElement, out: Prim[],
  ox: number, oy: number, clip: DOMRect | null,
  parentClipId?: string,
) {
  if (isSkipped(el)) return;
  const r = el.getBoundingClientRect();
  if (r.width < 1 || r.height < 1) return;
  if (clip && (r.bottom < clip.top || r.top > clip.bottom)) return;

  const s = window.getComputedStyle(el);
  const rx = bRadius(s);
  const ex = r.left - ox;
  const ey = r.top - oy;

  // Determine if this element clips its children (overflow: hidden/clip/auto/scroll)
  let childClipId = parentClipId;
  const overflow = s.overflow || s.overflowX || s.overflowY;
  if (overflow && (overflow.includes("hidden") || overflow.includes("clip"))) {
    childClipId = getOrCreateClip(ex, ey, r.width, r.height, rx);
  }

  // ── Box shadow (rendered BEHIND the element) ──
  const shadows = parseBoxShadows(s.boxShadow);
  for (const sh of shadows) {
    if (sh.inset) continue; // inset shadows rendered differently (skip for now)
    const shColor = sh.color;
    const shAlpha = getAlpha(sh.color) || 0.25;

    if (sh.blur > 0) {
      // Use SVG filter for blurred shadows
      const filterId = getOrCreateFilter(sh.blur, sh.x, sh.y);
      out.push({
        tag: "rect",
        x: ex - sh.spread, y: ey - sh.spread,
        w: r.width + sh.spread * 2, h: r.height + sh.spread * 2,
        fill: shColor, rx: rx + sh.spread,
        opacity: Math.min(shAlpha * 1.5, 0.6),
        filter: filterId,
      });
    } else {
      // Simple offset shadow without blur
      out.push({
        tag: "rect",
        x: ex + sh.x - sh.spread, y: ey + sh.y - sh.spread,
        w: r.width + sh.spread * 2, h: r.height + sh.spread * 2,
        fill: shColor, rx: rx + sh.spread,
        opacity: shAlpha,
      });
    }
  }

  // ── Background color ──
  const bg = parseColor(s.backgroundColor);
  if (bg) {
    const a = getAlpha(s.backgroundColor);
    out.push({
      tag: "rect", x: ex, y: ey, w: r.width, h: r.height,
      fill: bg, rx, opacity: a < 0.99 ? a : undefined,
      clipId: parentClipId,
    });
  }

  // ── Gradient ──
  if (s.backgroundImage && s.backgroundImage !== "none" && s.backgroundImage.includes("gradient")) {
    const gradPrim = parseGradient(s.backgroundImage, ex, ey, r.width, r.height);
    if (gradPrim) {
      gradPrim.rx = rx;
      out.push(gradPrim);
    } else if (!bg) {
      // Fallback: extract any color tokens via canvas
      const fallbackColor = extractFirstGradientColor(s.backgroundImage);
      if (fallbackColor) {
        out.push({
          tag: "rect", x: ex, y: ey, w: r.width, h: r.height,
          fill: fallbackColor, rx, opacity: 0.85,
        });
      }
    }
  }

  // ── Background image URL → placeholder ──
  if (s.backgroundImage && s.backgroundImage.startsWith("url(") && !bg) {
    out.push({
      tag: "rect", x: ex, y: ey, w: r.width, h: r.height,
      fill: "#e5e7eb", rx,
    });
  }

  // ── Border ──
  const bw = parseFloat(s.borderTopWidth) || 0;
  if (bw >= 0.5) {
    const bc = parseColor(s.borderTopColor);
    if (bc) {
      const effectiveStrokeW = Math.max(bw, 1.5);
      let effectiveBorderColor = bc;
      const borderLum = luminance(bc);
      if (borderLum > 0.75) {
        effectiveBorderColor = darkenHex(bc, 0.72);
      }

      // Card pattern: white bg + border + rounded corners
      if (bg && luminance(bg) > 0.95 && rx > 2) {
        out.push({
          tag: "rect", x: ex, y: ey, w: r.width, h: r.height,
          fill: "#f8f9fa", rx,
        });
      }

      out.push({
        tag: "rect", x: ex, y: ey, w: r.width, h: r.height,
        stroke: effectiveBorderColor, strokeW: effectiveStrokeW, rx,
      });
    }
  }

  // ── Outline (separate from border) ──
  const outlineWidth = parseFloat(s.outlineWidth) || 0;
  if (outlineWidth >= 0.5 && s.outlineStyle !== "none") {
    const oc = parseColor(s.outlineColor);
    if (oc) {
      const outlineOffset = parseFloat(s.outlineOffset) || 0;
      out.push({
        tag: "rect",
        x: ex - outlineWidth - outlineOffset,
        y: ey - outlineWidth - outlineOffset,
        w: r.width + (outlineWidth + outlineOffset) * 2,
        h: r.height + (outlineWidth + outlineOffset) * 2,
        stroke: oc, strokeW: Math.max(outlineWidth, 1), rx: rx + outlineOffset,
      });
    }
  }

  // ── Special elements: HR, VIDEO, IFRAME ──
  const tag = el.tagName;
  if (tag === "HR") {
    const hrColor = parseColor(s.borderTopColor) || parseColor(s.color) || "#d1d5db";
    const hrH = Math.max(r.height, 1);
    out.push({
      tag: "rect", x: ex, y: ey, w: r.width, h: hrH,
      fill: hrColor,
    });
  }

  if (tag === "VIDEO" || tag === "IFRAME") {
    // Render a placeholder with an icon-like indicator
    if (!bg) {
      out.push({
        tag: "rect", x: ex, y: ey, w: r.width, h: r.height,
        fill: "#f3f4f6", stroke: "#d1d5db", strokeW: 1, rx,
      });
    }
    // Cross lines to indicate media placeholder
    out.push({
      tag: "rect", x: ex + r.width * 0.35, y: ey + r.height * 0.4,
      w: r.width * 0.3, h: r.height * 0.2,
      fill: "#9ca3af", rx: 4,
    });
  }

  // Recurse children
  for (let i = 0; i < el.children.length; i++) {
    const ch = el.children[i];
    if (ch instanceof HTMLElement) collectBg(ch, out, ox, oy, clip, childClipId);
  }
}

/** Extract first color from a gradient string as fallback */
function extractFirstGradientColor(bgImage: string): string | null {
  // Try various color patterns in the gradient
  // 1. hex colors
  const hexMatch = bgImage.match(/#[0-9a-fA-F]{3,8}/);
  if (hexMatch) return parseColor(hexMatch[0]);

  // 2. rgb/rgba (both comma and space formats)
  const rgbMatch = bgImage.match(/rgba?\([^)]+\)/);
  if (rgbMatch) return parseColor(rgbMatch[0]);

  // 3. oklch, hsl, lab, etc.
  const funcMatch = bgImage.match(/(oklch|oklab|hsl|hsla|lab|lch|hwb|color)\([^)]+\)/);
  if (funcMatch) return parseColor(funcMatch[0]);

  // 4. Named colors
  const namedMatch = bgImage.match(/,\s*([a-z]+)\s/i);
  if (namedMatch) return parseColor(namedMatch[1]);

  return null;
}

/* ══════════════════════════════════════════
   Pass 2 — SVG elements (icons, graphics)
   ══════════════════════════════════════════ */

function collectSvgs(
  root: HTMLElement, out: Prim[],
  ox: number, oy: number, clip: DOMRect | null,
) {
  const svgs = root.querySelectorAll("svg");
  const serializer = new XMLSerializer();

  for (const svg of svgs) {
    if (ancestorSkipped(svg)) continue;

    const rect = svg.getBoundingClientRect();
    if (rect.width < 2 || rect.height < 2) continue;
    if (clip && (rect.bottom < clip.top || rect.top > clip.bottom)) continue;

    try {
      const clone = svg.cloneNode(true) as SVGElement;
      clone.setAttribute("width", String(Math.round(rect.width)));
      clone.setAttribute("height", String(Math.round(rect.height)));
      clone.removeAttribute("class");

      // Resolve currentColor from parent element
      const parentEl = svg.parentElement;
      let currentColor = "#000000";
      if (parentEl) {
        const pc = parseColor(window.getComputedStyle(parentEl).color);
        if (pc) currentColor = pc;
      }

      let svgStr = serializer.serializeToString(clone);
      svgStr = svgStr.replace(/currentColor/gi, currentColor);

      if (!svgStr.includes("xmlns")) {
        svgStr = svgStr.replace("<svg", '<svg xmlns="http://www.w3.org/2000/svg"');
      }

      const encoded = btoa(unescape(encodeURIComponent(svgStr)));
      const dataUrl = "data:image/svg+xml;base64," + encoded;

      out.push({
        tag: "image",
        x: rect.left - ox, y: rect.top - oy,
        w: rect.width, h: rect.height,
        href: dataUrl,
      });
    } catch (err) {
      console.warn("[FigmaExport] SVG serialize failed:", err);
    }
  }
}

/* ══════════════════════════════════════════
   Pass 3 — Images
   ══════════════════════════════════════════ */

async function collectImg(
  el: HTMLElement, out: Prim[],
  ox: number, oy: number, clip: DOMRect | null,
  embed: boolean,
) {
  if (isSkipped(el)) return;
  const r = el.getBoundingClientRect();
  if (r.width < 4 || r.height < 4) return;
  if (clip && (r.bottom < clip.top || r.top > clip.bottom)) return;

  if (el.tagName === "IMG") {
    const imgRx = bRadius(window.getComputedStyle(el));
    if (embed) {
      const d = await imgDataUrl((el as HTMLImageElement).src);
      if (d) {
        // If image has border-radius, create a clip
        let clipId: string | undefined;
        if (imgRx > 2) {
          clipId = getOrCreateClip(r.left - ox, r.top - oy, r.width, r.height, imgRx);
        }
        out.push({
          tag: "image", x: r.left - ox, y: r.top - oy, w: r.width, h: r.height, href: d,
          clipId,
        });
        return;
      }
    }
    out.push({
      tag: "rect", x: r.left - ox, y: r.top - oy, w: r.width, h: r.height,
      fill: "#f3f4f6", stroke: "#d1d5db", strokeW: 1, rx: imgRx,
    });
    return;
  }

  for (let i = 0; i < el.children.length; i++) {
    const ch = el.children[i];
    if (ch instanceof HTMLElement) await collectImg(ch, out, ox, oy, clip, embed);
  }
}

function imgDataUrl(src: string): Promise<string | null> {
  return new Promise(res => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const c = document.createElement("canvas");
        c.width = img.naturalWidth; c.height = img.naturalHeight;
        const cx = c.getContext("2d");
        if (!cx) return res(null);
        cx.drawImage(img, 0, 0);
        res(c.toDataURL("image/png"));
      } catch { res(null); }
    };
    img.onerror = () => res(null);
    img.src = src;
  });
}

/* ══════════════════════════════════════════
   Pass 4 — Text via TreeWalker + Range
   ══════════════════════════════════════════ */

function getVisualLines(textNode: Text): Array<{ text: string; rect: DOMRect }> {
  const full = textNode.textContent || "";
  if (!full.trim()) return [];

  const range = document.createRange();
  range.selectNodeContents(textNode);
  const allRects = range.getClientRects();
  if (allRects.length === 0) return [];

  if (allRects.length === 1) {
    return [{ text: full.trim(), rect: allRects[0] }];
  }

  const lineTops: number[] = [];
  for (const r of allRects) {
    if (r.width < 0.5 || r.height < 0.5) continue;
    if (!lineTops.some(t => Math.abs(t - r.top) < 4)) lineTops.push(r.top);
  }
  lineTops.sort((a, b) => a - b);

  if (lineTops.length <= 1) {
    let minL = Infinity, maxR = -Infinity;
    const top = allRects[0].top, h = allRects[0].height;
    for (const r of allRects) { if (r.width < 0.5) continue; minL = Math.min(minL, r.left); maxR = Math.max(maxR, r.right); }
    return [{ text: full.trim(), rect: new DOMRect(minL, top, maxR - minL, h) }];
  }

  const lines: Array<{ text: string; rect: DOMRect }> = [];
  let lineStart = 0;
  let prevTop = -9999;
  let lMinL = Infinity, lMaxR = -Infinity, lTop = 0, lH = 0;
  const cr = document.createRange();

  for (let i = 0; i < full.length; i++) {
    cr.setStart(textNode, i);
    cr.setEnd(textNode, Math.min(i + 1, full.length));
    const cRect = cr.getBoundingClientRect();
    if (cRect.width < 0.1 && cRect.height < 0.1) continue;

    const isNew = prevTop > -9000 && Math.abs(cRect.top - prevTop) > 4;

    if (isNew && i > lineStart) {
      const t = full.substring(lineStart, i).trim();
      if (t) lines.push({ text: t, rect: new DOMRect(lMinL, lTop, lMaxR - lMinL, lH) });
      lineStart = i;
      lMinL = Infinity; lMaxR = -Infinity;
    }

    lMinL = Math.min(lMinL, cRect.left);
    lMaxR = Math.max(lMaxR, cRect.right);
    lTop = cRect.top; lH = cRect.height;
    prevTop = cRect.top;
  }

  if (lineStart < full.length) {
    const t = full.substring(lineStart).trim();
    if (t) lines.push({ text: t, rect: new DOMRect(lMinL, lTop, lMaxR - lMinL, lH) });
  }

  return lines;
}

function collectText(
  root: HTMLElement, out: Prim[],
  ox: number, oy: number, clip: DOMRect | null,
) {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let node: Node | null;

  while ((node = walker.nextNode())) {
    const tn = node as Text;
    const text = tn.textContent;
    if (!text || !text.trim()) continue;

    const parent = tn.parentElement;
    if (!parent) continue;
    if (parent.closest("svg") || parent.closest("script") || parent.closest("style") || parent.closest("noscript")) continue;
    if (ancestorSkipped(parent)) continue;

    const style = window.getComputedStyle(parent);
    if (style.display === "none" || style.visibility === "hidden") continue;

    const fontSize = parseFloat(style.fontSize) || 16;
    const fontWeight = style.fontWeight || "400";
    const rawFam = style.fontFamily || "sans-serif";
    const fontFamily = rawFam.split(",")[0].replace(/['"]/g, "").trim();
    const color = parseColor(style.color) || "#000000";

    // Detect text-decoration
    const td = style.textDecorationLine || style.textDecoration || "";
    let textDecoration: string | undefined;
    if (td.includes("underline")) textDecoration = "underline";
    else if (td.includes("line-through")) textDecoration = "line-through";

    const vLines = getVisualLines(tn);

    for (const line of vLines) {
      if (!line.text) continue;
      const r = line.rect;
      if (r.width < 1 || r.height < 1) continue;
      if (clip && (r.bottom < clip.top || r.top > clip.bottom)) continue;

      const baseline = r.height * 0.78;

      out.push({
        tag: "text",
        x: r.left - ox,
        y: r.top + baseline - oy,
        content: line.text,
        fontSize, fontWeight, fontFamily,
        fill: color,
        textDecoration,
      });

      // Render text-decoration as a line primitive
      if (textDecoration === "underline") {
        const lineY = r.top + r.height * 0.9 - oy;
        out.push({
          tag: "rect",
          x: r.left - ox, y: lineY,
          w: r.width, h: Math.max(1, fontSize * 0.07),
          fill: color,
        });
      } else if (textDecoration === "line-through") {
        const lineY = r.top + r.height * 0.55 - oy;
        out.push({
          tag: "rect",
          x: r.left - ox, y: lineY,
          w: r.width, h: Math.max(1, fontSize * 0.07),
          fill: color,
        });
      }
    }
  }
}

/* ══════════════════════════════════════════
   Contrast fix pass
   ══════════════════════════════════════════ */

function fixContrast(items: Prim[]): void {
  // Collect REAL background areas — both solid rects and gradient rects.
  // Exclude shadow rects (have filter), very low opacity rects, and tiny decoration rects.
  interface BgArea { x: number; y: number; w: number; h: number; color: string; }
  const bgAreas: BgArea[] = [];

  for (const it of items) {
    if (it.tag === "rect" && it.fill && !it.stroke && !it.filter) {
      if (it.opacity !== undefined && it.opacity < 0.5) continue;
      if (it.w < 10 || it.h < 10) continue;
      bgAreas.push({ x: it.x, y: it.y, w: it.w, h: it.h, color: it.fill });
    }
    // Also include gradient rects — use darkest stop color as representative
    if (it.tag === "gradientRect") {
      const grad = _gradients.find(g => g.id === it.gradientId);
      if (grad && grad.stops.length > 0) {
        // Pick the stop with lowest luminance (darkest) as the bg representative
        let darkestColor = grad.stops[0].color;
        let darkestLum = luminance(darkestColor);
        for (const stop of grad.stops) {
          const l = luminance(stop.color);
          if (l < darkestLum) { darkestLum = l; darkestColor = stop.color; }
        }
        bgAreas.push({ x: it.x, y: it.y, w: it.w, h: it.h, color: darkestColor });
      }
    }
  }

  for (const it of items) {
    if (it.tag !== "text") continue;

    let bgHex = "#ffffff";
    for (const bg of bgAreas) {
      if (
        it.x >= bg.x - 2 && it.x <= bg.x + bg.w + 2 &&
        it.y >= bg.y - 2 && it.y <= bg.y + bg.h + 2
      ) {
        bgHex = bg.color;
      }
    }

    const textLum = luminance(it.fill);
    const bgLum = luminance(bgHex);
    const ratio = contrastRatio(textLum, bgLum);

    if (ratio < 2.0) {
      // Only flip when contrast is really bad (< 2.0 instead of 2.5)
      const whiteRatio = contrastRatio(1, bgLum);
      const blackRatio = contrastRatio(0, bgLum);
      it.fill = whiteRatio > blackRatio ? "#ffffff" : "#1a1a2e";
    }
  }
}

/* ═════════════════════════════════════════
   SVG Builder
   ══════════════════════════════════════════ */

function esc(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

function nn(v: number): string { return v.toFixed(1); }

function buildSvg(items: Prim[], w: number, h: number): string {
  const p: string[] = [];

  p.push(
    `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"` +
    ` width="${Math.round(w)}" height="${Math.round(h)}"` +
    ` viewBox="0 0 ${Math.round(w)} ${Math.round(h)}">`
  );

  // ── Defs section ──
  if (_gradients.length > 0 || _filters.length > 0 || _clips.length > 0) {
    p.push("<defs>");

    // Gradients
    for (const g of _gradients) {
      if (g.type === "linear") {
        // Convert angle to SVG x1,y1,x2,y2
        const rad = ((g.angle || 180) - 90) * (Math.PI / 180);
        const x1 = 50 - Math.cos(rad) * 50;
        const y1 = 50 - Math.sin(rad) * 50;
        const x2 = 50 + Math.cos(rad) * 50;
        const y2 = 50 + Math.sin(rad) * 50;
        p.push(`<linearGradient id="${g.id}" x1="${x1.toFixed(1)}%" y1="${y1.toFixed(1)}%" x2="${x2.toFixed(1)}%" y2="${y2.toFixed(1)}%">`);
        for (const s of g.stops) {
          p.push(`  <stop offset="${s.offset}" stop-color="${s.color}"/>`);
        }
        p.push("</linearGradient>");
      } else {
        p.push(`<radialGradient id="${g.id}" cx="50%" cy="50%" r="50%">`);
        for (const s of g.stops) {
          p.push(`  <stop offset="${s.offset}" stop-color="${s.color}"/>`);
        }
        p.push("</radialGradient>");
      }
    }

    // Filters (for box-shadow blur)
    for (const f of _filters) {
      const margin = Math.ceil(f.blur * 2 + Math.abs(f.dx) + Math.abs(f.dy));
      const pct = Math.ceil((margin / 100) * 100 + 50);
      p.push(`<filter id="${f.id}" x="-${pct}%" y="-${pct}%" width="${200 + pct * 2}%" height="${200 + pct * 2}%">`);
      if (f.dx !== 0 || f.dy !== 0) {
        p.push(`  <feOffset dx="${f.dx}" dy="${f.dy}"/>`);
      }
      p.push(`  <feGaussianBlur stdDeviation="${f.blur.toFixed(1)}"/>`);
      p.push("</filter>");
    }

    // Clips (for overflow:hidden / border-radius clipping)
    for (const c of _clips) {
      p.push(`<clipPath id="${c.id}">`);
      p.push(`  <rect x="${nn(c.x)}" y="${nn(c.y)}" width="${nn(c.w)}" height="${nn(c.h)}" rx="${nn(c.rx)}"/>`);
      p.push("</clipPath>");
    }

    p.push("</defs>");
  }

  // White background
  p.push(`<rect width="${Math.round(w)}" height="${Math.round(h)}" fill="#fff"/>`);

  for (const it of items) {
    switch (it.tag) {
      case "rect": {
        const a: string[] = [`x="${nn(it.x)}" y="${nn(it.y)}" width="${nn(it.w)}" height="${nn(it.h)}"`];
        if (it.fill) a.push(`fill="${it.fill}"`); else a.push(`fill="none"`);
        if (it.stroke) a.push(`stroke="${it.stroke}"`);
        if (it.strokeW) a.push(`stroke-width="${it.strokeW}"`);
        if (it.rx) a.push(`rx="${nn(it.rx)}"`);
        if (it.opacity !== undefined) a.push(`opacity="${it.opacity.toFixed(2)}"`);
        if (it.filter) a.push(`filter="url(#${it.filter})"`);
        if (it.clipId) a.push(`clip-path="url(#${it.clipId})"`);
        p.push(`<rect ${a.join(" ")}/>`);
        break;
      }
      case "gradientRect": {
        const a: string[] = [
          `x="${nn(it.x)}" y="${nn(it.y)}" width="${nn(it.w)}" height="${nn(it.h)}"`,
          `fill="url(#${it.gradientId})"`,
        ];
        if (it.rx) a.push(`rx="${nn(it.rx)}"`);
        if (it.opacity !== undefined) a.push(`opacity="${it.opacity.toFixed(2)}"`);
        p.push(`<rect ${a.join(" ")}/>`);
        break;
      }
      case "image": {
        let attrs = `x="${nn(it.x)}" y="${nn(it.y)}" width="${nn(it.w)}" height="${nn(it.h)}"` +
          ` href="${it.href}" preserveAspectRatio="xMidYMid slice"`;
        if (it.clipId) attrs += ` clip-path="url(#${it.clipId})"`;
        p.push(`<image ${attrs}/>`);
        break;
      }
      case "text": {
        let decoration = "";
        if (it.textDecoration === "underline") decoration = ` text-decoration="underline"`;
        else if (it.textDecoration === "line-through") decoration = ` text-decoration="line-through"`;
        p.push(
          `<text x="${nn(it.x)}" y="${nn(it.y)}"` +
          ` font-size="${it.fontSize}"` +
          ` font-weight="${it.fontWeight}"` +
          ` font-family="${esc(it.fontFamily)}"` +
          ` fill="${it.fill}"` +
          ` text-anchor="start"` +
          `${decoration}` +
          `>${esc(it.content)}</text>`
        );
        break;
      }
    }
  }

  p.push("</svg>");
  return p.join("\n");
}

/* ══════════════════════════════════════════
   Public API
   ══════════════════════════════════════════ */

export async function generateFigmaSvg(
  options: FigmaExportOptions = {},
): Promise<{ svg: string; width: number; height: number }> {
  const isFullPage = options.mode === "fullpage";
  const savedScroll = window.scrollY;

  // Reset shared defs state
  resetDefs();

  // For full page: scroll through to trigger lazy loading
  if (isFullPage) {
    const totalH = Math.max(document.body.scrollHeight, document.documentElement.scrollHeight);
    const step = Math.floor(window.innerHeight * 0.8);
    for (let y = 0; y < totalH; y += step) {
      window.scrollTo(0, y);
      await new Promise(r => setTimeout(r, 50));
    }
    window.scrollTo(0, 0);
    await new Promise(r => setTimeout(r, 150));
  }

  let width: number;
  let height: number;
  const ox = 0;
  const oy = 0;
  let clip: DOMRect | null = null;

  if (isFullPage) {
    width = Math.max(document.body.scrollWidth, document.documentElement.scrollWidth, document.body.offsetWidth);
    height = Math.max(document.body.scrollHeight, document.documentElement.scrollHeight, document.body.offsetHeight);
  } else {
    width = window.innerWidth;
    height = window.innerHeight;
    clip = new DOMRect(0, 0, width, height);
  }

  const items: Prim[] = [];

  // 1. Backgrounds, borders, shadows, outlines
  collectBg(document.body, items, ox, oy, clip);

  // 2. SVG elements (icons, graphics)
  collectSvgs(document.body, items, ox, oy, clip);

  // 3. Raster images
  await collectImg(document.body, items, ox, oy, clip, !!options.includeImages);

  // 4. Text (on top) with text-decoration
  collectText(document.body, items, ox, oy, clip);

  // 5. Fix text contrast
  fixContrast(items);

  // Restore scroll
  if (isFullPage) window.scrollTo(0, savedScroll);

  // Adjust height for full-page
  if (isFullPage) {
    for (const it of items) {
      if (it.tag === "rect") height = Math.max(height, it.y + it.h);
      if (it.tag === "text") height = Math.max(height, it.y + it.fontSize);
      if (it.tag === "image") height = Math.max(height, it.y + it.h);
      if (it.tag === "gradientRect") height = Math.max(height, it.y + it.h);
    }
    height += 20;
  }

  const svg = buildSvg(items, width, height);
  return { svg, width, height };
}

/** Copy SVG to clipboard using document.execCommand("copy"). */
export function copySvgToClipboard(svgString: string): boolean {
  const ta = document.createElement("textarea");
  ta.value = svgString;
  ta.style.cssText = "position:fixed;left:-9999px;top:-9999px;opacity:0";
  document.body.appendChild(ta);
  ta.select();
  let ok = false;
  try { ok = document.execCommand("copy"); } catch { /* */ }
  document.body.removeChild(ta);
  return ok;
}

/** Download SVG as a file. */
export function downloadSvgFile(svgString: string, filename: string): void {
  const blob = new Blob([svgString], { type: "image/svg+xml;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.style.display = "none";
  document.body.appendChild(a); a.click();
  document.body.removeChild(a); URL.revokeObjectURL(url);
}